const ALGORITHM = 'AES-GCM';
const KEY_BITS = 256;
const IV_BYTES = 12;
export function toBase64(bytes) {
    let binary = '';
    for (const b of bytes)
        binary += String.fromCharCode(b);
    return btoa(binary);
}
// Returns Uint8Array<ArrayBuffer> rather than Uint8Array<ArrayBufferLike> so it
// satisfies BufferSource: Web Crypto will not accept a possibly-shared buffer.
export function fromBase64(b64) {
    const binary = atob(b64);
    const out = new Uint8Array(new ArrayBuffer(binary.length));
    for (let i = 0; i < binary.length; i++)
        out[i] = binary.charCodeAt(i);
    return out;
}
export async function generateQuestionKey() {
    const key = await crypto.subtle.generateKey({ name: ALGORITHM, length: KEY_BITS }, true, [
        'encrypt',
        'decrypt',
    ]);
    const raw = await crypto.subtle.exportKey('raw', key);
    return toBase64(new Uint8Array(raw));
}
async function importKey(base64Key, usage) {
    return crypto.subtle.importKey('raw', fromBase64(base64Key), { name: ALGORITHM }, false, [usage]);
}
/**
 * Server side. The body deliberately has no correctAnswer field -- the answer is
 * never in the ciphertext, encrypted or otherwise. It arrives only in the reveal
 * payload, after the host closes the question.
 */
export async function encryptQuestionBody(base64Key, body) {
    const key = await importKey(base64Key, 'encrypt');
    const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
    const plaintext = new TextEncoder().encode(JSON.stringify(body));
    const buf = await crypto.subtle.encrypt({ name: ALGORITHM, iv }, key, plaintext);
    return { iv: toBase64(iv), ciphertext: toBase64(new Uint8Array(buf)) };
}
/** Client side, once the key arrives in a state poll. */
export async function decryptQuestionBody(base64Key, question) {
    const key = await importKey(base64Key, 'decrypt');
    const buf = await crypto.subtle.decrypt({ name: ALGORITHM, iv: fromBase64(question.iv) }, key, fromBase64(question.ciphertext));
    return JSON.parse(new TextDecoder().decode(buf));
}
/**
 * Media, sealed with the same per-question key as the body.
 *
 * This is what lets a 2MB video sit on 75 handsets half an hour before the round
 * and still be unreadable: the phone prefetches the ciphertext at the door and
 * the key does not arrive until the host opens the question. The blip-resilience
 * of caching, without letting anyone look ahead.
 *
 * The IV is prepended to the bytes rather than sent in a header, so the whole
 * thing is one opaque blob to store and one to hand back -- a cached response
 * cannot get separated from the IV that decrypts it.
 */
export async function sealMedia(base64Key, plaintext) {
    const key = await importKey(base64Key, 'encrypt');
    const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
    const buf = await crypto.subtle.encrypt({ name: ALGORITHM, iv }, key, plaintext);
    const out = new Uint8Array(new ArrayBuffer(IV_BYTES + buf.byteLength));
    out.set(iv, 0);
    out.set(new Uint8Array(buf), IV_BYTES);
    return out;
}
/** Client side, at Open. Returns bytes ready to wrap in a Blob. */
export async function unsealMedia(base64Key, sealed) {
    const key = await importKey(base64Key, 'decrypt');
    const iv = sealed.subarray(0, IV_BYTES);
    const body = sealed.subarray(IV_BYTES);
    const buf = await crypto.subtle.decrypt({ name: ALGORITHM, iv }, key, body);
    return new Uint8Array(buf);
}
//# sourceMappingURL=quizCrypto.js.map